<template>
    <Dialog :open="isOpen" @update:open="handleOpenChange">
        <DialogContent :class="cn('p-0 max-h-90-dynamic flex flex-col', modalWidthClass)" @pointerDownOutside="(e) => e.preventDefault()">
            <!-- Modal Header -->
            <DialogHeader class="border-b p-4 flex justify-between items-center flex-shrink-0">
                <div class="grow">
                    <DialogTitle v-if="title" class="font-medium text-lg" v-html="sanitizeHtml(title)" />
                    <DialogDescription
                        class="text-sm text-muted-foreground mt-1"
                        :class="{ 'sr-only': !description }"
                        v-html="sanitizeHtml(description || title || 'Modal dialog')" />
                </div>
                <div class="flex items-center space-x-2">
                    <slot name="header"></slot>
                </div>
            </DialogHeader>

            <!-- Scrollable Content Area -->
            <div class="p-4 overflow-auto flex-1 min-h-0">
                <slot></slot>
            </div>

            <!-- Footer Buttons -->
            <DialogFooter v-if="showFooterButtons" class="border-t p-4 flex justify-end space-x-2 flex-shrink-0 bg-background rounded-b-lg">
                <slot name="footer"></slot>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup>
import { computed } from 'vue';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    showFooterButtons: {
        type: Boolean,
        default: true
    },
    title: {
        type: String,
        default: null
    },
    description: {
        type: String,
        default: ''
    },
    size: {
        type: String,
        default: 'md'
    },
    position: {
        type: String,
        default: 'center'
    }
});

const emit = defineEmits(['update:isOpen']);

const modalWidthClass = computed(() => {
    switch (props.size) {
        case 'sm':
            return '!max-w-sm w-full'; // ~384px
        case 'lg':
            return '!max-w-lg w-full'; // ~512px
        case 'xl':
            return '!max-w-xl w-full'; // ~576px
        case '2xl':
            return '!max-w-2xl w-full'; // ~672px
        case '3xl':
            return '!max-w-3xl w-full'; // ~768px
        case '4xl':
            return '!max-w-4xl w-full'; // ~896px
        case '5xl':
            return '!max-w-5xl w-full'; // ~1024px
        case 'xxl':
            return '!max-w-6xl w-full'; // ~1152px
        case 'md':
        default:
            return '!max-w-md w-full'; // ~448px
    }
});

function sanitizeHtml(html) {
    if (!html) return '';
    if (!_.isNaN(Number(html))) {
        return html;
    }
    return DOMPurify.sanitize(html);
}

function handleOpenChange(open) {
    emit('update:isOpen', open);
}
</script>
